import type { ReactNode } from 'react';
import { useState } from 'react';
import { Alert } from '@/components/ui/Alert';
import { StatusBadge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { CardTableArea } from '@/components/ui/Card';
import { Modal } from '@/components/ui/Modal';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeaderCell,
  TableRow,
} from '@/components/ui/Table';
import { useActiveTenantAccess } from '@/hooks/use-active-tenant';
import { getErrorMessage } from '@/lib/api-error';
import { formatDateTime } from '@/lib/format';
import { ROUTES } from '@/routes/paths';
import { orderStatus } from '@/theme/status';
import type { Order, OrderStatusHistoryEntry } from '@/types/orders';
import {
  campoFalloDropi,
  canAttachGuide,
  describeHistoryEntry,
  describeVariants,
  formatNumber,
  motivoCancelacionLabel,
  nextEstados,
  orderAddressLines,
} from './orders-format';
import { useOrder } from './use-orders';
import { DropiHuellaBadge, describeHuella } from './DropiHuellaBadge';
import { useDropiHuella } from '../integrations/use-dropi';

/**
 * Ficha de una orden: el pedido completo y su historial de estados.
 *
 * El detalle viene de `GET .../orders/:orderId` (hook `useOrder`) y, mientras llega, se pinta la fila
 * que ya estaba en la tabla: así el diálogo abre al instante y se completa solo. Es la pantalla donde
 * se responde "¿qué pidió y por dónde va?": productos con precio unitario y subtotal, total, entrega,
 * pago, la guía cargada y **quién movió la orden y por qué** (`order_status_history`).
 *
 * Los botones de escritura (cambiar estado / cargar la guía) solo se pintan si el usuario es ADMIN y
 * la orden admite esa acción: `nextEstados` y `canAttachGuide` son los espejos de la máquina de
 * estados del backend, así que no se ofrece un botón que vaya a responder 400. El backend valida
 * igual (403 sin ADMIN, 400/409 en la transición y en la guía).
 */

export interface OrderDetailDialogProps {
  open: boolean;
  onClose: () => void;
  /** Orden seleccionada en la tabla (`null` = diálogo cerrado). */
  order: Order | null;
  /** Abre el diálogo de cambio de estado (solo si el usuario es ADMIN y hay destinos). */
  onChangeStatus?: (order: Order) => void;
  /** Abre el diálogo de la guía (solo si el usuario es ADMIN y el estado la acepta). */
  onAttachGuide?: (order: Order) => void;
  /** Abre el diálogo para enviar la orden a Dropi (solo si el usuario es ADMIN). */
  onSendToDropi?: (order: Order) => void;
}

/** Bloque de la ficha: título + contenido. */
function DetailSection({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="space-y-2 border-t border-line pt-4">
      <h3 className="text-micro font-semibold tracking-wide text-ink-faint uppercase">{title}</h3>
      {children}
    </section>
  );
}

/** Fila etiqueta/valor (cliente, entrega, pago, guía). */
function DetailRow({ label, value, mono = false }: { label: string; value: ReactNode; mono?: boolean }) {
  return (
    <div className="flex justify-between gap-4">
      <dt className="text-ink-muted">{label}</dt>
      <dd className={mono ? 'truncate text-ink tabular' : 'text-right text-ink'}>{value}</dd>
    </div>
  );
}

/** Línea con vínculo (teléfono, conversación de origen). */
function DetailLink({ href, children }: { href: string; children: ReactNode }) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noreferrer"
      className="text-brand-600 hover:underline"
      title={href}
    >
      {children}
    </a>
  );
}

export function OrderDetailDialog({
  open,
  onClose,
  order,
  onChangeStatus,
  onAttachGuide,
  onSendToDropi,
}: OrderDetailDialogProps) {
  const { tenantId, canEdit } = useActiveTenantAccess();

  /**
   * El detalle fresco del endpoint. Mientras llega (o si falla) se usa la fila de la tabla, que ya
   * trae todo menos el historial: el diálogo nunca aparece vacío.
   */
  const detailQuery = useOrder(tenantId, order?.id ?? null);
  const detail = detailQuery.data ?? order;

  if (!detail) return null;

  const actual = orderStatus[detail.estado];
  const items = Array.isArray(detail.productos) ? detail.productos : [];
  /** El listado no trae historial: hasta que llegue el detalle, la sección lo dice. */
  const historial: OrderStatusHistoryEntry[] = detailQuery.data?.statusHistory ?? [];
  const destinos = nextEstados(detail.estado);
  const addressLines = orderAddressLines(detail);

  /**
   * Huella de Dropi. Primero la que ya viene con el detalle (caché del backend, gratis). Si no hay,
   * el admin puede pedirla con el botón: recién ahí se llama a Dropi (`forzar` no hace falta, la
   * caché del backend ya decide si el informe guardado sirve).
   */
  const [consultarHuella, setConsultarHuella] = useState(false);
  const huellaQuery = useDropiHuella(tenantId, detail.telefono, consultarHuella);
  const huella = detailQuery.data?.huella ?? huellaQuery.data ?? null;

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="lg"
      title={`Orden de ${detail.clienteNombre}`}
      description={`${actual.label} · ${formatNumber(detail.total)} · ${formatDateTime(detail.createdAt)}`}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cerrar
          </Button>

          {canEdit && destinos.length > 0 && onChangeStatus ? (
            <Button variant="secondary" onClick={() => onChangeStatus(detail)}>
              Cambiar estado
            </Button>
          ) : null}

          {canEdit && canAttachGuide(detail.estado) && onAttachGuide ? (
            <Button onClick={() => onAttachGuide(detail)}>
              {detail.guiaEnvioFotoUrl ? 'Reemplazar la guía' : 'Cargar la guía'}
            </Button>
          ) : null}

          {/* Dropi (docs/DROPI.md): abre el diálogo con la previsualización (o el estado real si la
              orden ya está en Dropi). El envío es una acción explícita del admin, no automática. */}
          {canEdit && onSendToDropi ? (
            <Button
              variant={canAttachGuide(detail.estado) ? 'secondary' : 'primary'}
              onClick={() => onSendToDropi(detail)}
            >
              {detail.dropiOrderId ? 'Ver el estado en Dropi' : 'Enviar a Dropi'}
            </Button>
          ) : null}
        </>
      }
    >
      <div className="space-y-4">
        {detailQuery.isError ? <Alert>{getErrorMessage(detailQuery.error)}</Alert> : null}

        <div className="flex flex-wrap items-center gap-2">
          <StatusBadge label={actual.label} tone={actual.tone} />
          <span className="text-caption text-ink-muted">
            {detail.conversationId ? 'Pedido nacido del chat' : 'Cargada a mano (sin conversación)'}
          </span>
        </div>

        {/* Por qué se canceló (3-oct-2026): el motivo queda en la orden, no solo en el historial, para
            que al mirarla de un vistazo se sepa si fue una prueba, un cliente que se arrepintió o algo
            que no se pudo cobrar. */}
        {detail.motivoCancelacion ? (
          <Alert tone="warning">
            <strong>Cancelada:</strong> {motivoCancelacionLabel(detail.motivoCancelacion)}.
          </Alert>
        ) : null}

        {/* Huella digital de Dropi (docs/DROPI.md): viene en el detalle desde el caché del backend.
            Es lo que se mira al confirmar un pedido contraentrega: cuántas órdenes ha recibido el
            cliente y cuántas ha devuelto. Si todavía no hay informe, no se pinta nada. */}
        {huella ? (
          <div className="flex flex-wrap items-center gap-2">
            <DropiHuellaBadge huella={huella} />
            <span className="text-caption text-ink-faint">{describeHuella(huella)}</span>
          </div>
        ) : detail.telefono ? (
          <div className="flex flex-wrap items-center gap-2">
            <Button
              variant="secondary"
              size="sm"
              loading={huellaQuery.isFetching}
              onClick={() => setConsultarHuella(true)}
            >
              Consultar huella en Dropi
            </Button>
            <span className="text-caption text-ink-faint">
              Historial del cliente según Dropi (lo que se mira antes de despachar contraentrega).
            </span>
          </div>
        ) : null}

        {huellaQuery.isError ? (
          <Alert tone="warning">
            No se pudo consultar la huella: {getErrorMessage(huellaQuery.error)}
          </Alert>
        ) : null}

        {/* Estado en Dropi: la orden se crea allá pendiente de confirmación y el estado se refresca
            con el botón del pie. */}
        {detail.dropiOrderId ? (
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1 rounded-control border border-line bg-surface-muted px-3 py-2 text-caption text-ink-muted">
            <span className="font-medium text-ink">En Dropi #{detail.dropiOrderId}</span>
            {detail.dropiEstado ? <span>· {detail.dropiEstado}</span> : null}
            {detail.dropiFlete ? <span>· flete {formatNumber(detail.dropiFlete)}</span> : null}
            {detail.dropiTransportadora ? <span>· {detail.dropiTransportadora}</span> : null}
          </div>
        ) : null}

        {/* NOTA del último fallo al enviar a Dropi: qué campo falló, en palabras, y la nota accionable
            que armó el backend (ciudad/departamento, bodega del proveedor, producto sin vincular…).
            Queda guardada en la orden, así que el admin la ve al volver. */}
        {detail.dropiError ? (
          <Alert tone="warning">
            <p>
              <strong>
                {campoFalloDropi(detail.dropiErrorCampo) ?? 'No se pudo enviar a Dropi'}:
              </strong>{' '}
              {detail.dropiError}
            </p>
            <p className="mt-1 text-caption">
              Corregí eso y volvé a enviarla: la nota queda guardada en la orden.
            </p>
          </Alert>
        ) : null}

        <DetailSection title="Productos del pedido">
          {items.length === 0 ? (
            <p className="text-caption text-ink-faint">El pedido no tiene productos cargados.</p>
          ) : (
            <CardTableArea>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableHeaderCell>Producto</TableHeaderCell>
                    <TableHeaderCell align="right">Cantidad</TableHeaderCell>
                    <TableHeaderCell align="right">Precio unitario</TableHeaderCell>
                    <TableHeaderCell align="right">Subtotal</TableHeaderCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {items.map((item, index) => {
                    const variantes = describeVariants(item.variantes);

                    return (
                      <TableRow key={`${item.nombre}-${index}`}>
                        <TableCell>
                          <span className="flex flex-col gap-0.5">
                            <span className="font-medium text-ink">{item.nombre}</span>
                            {variantes ? (
                              <span className="text-caption text-ink-muted">{variantes}</span>
                            ) : null}
                            {item.obsequio ? (
                              <span className="text-caption text-ink-muted">
                                {`Obsequio: ${item.obsequio}`}
                              </span>
                            ) : null}
                          </span>
                        </TableCell>

                        <TableCell align="right" className="tabular">
                          {formatNumber(item.cantidad)}
                        </TableCell>

                        <TableCell align="right" className="tabular">
                          {typeof item.precioUnitario === 'number'
                            ? formatNumber(item.precioUnitario)
                            : '—'}
                        </TableCell>

                        <TableCell align="right" className="tabular">
                          {typeof item.subtotal === 'number' ? formatNumber(item.subtotal) : '—'}
                        </TableCell>
                      </TableRow>
                    );
                  })}

                  <TableRow>
                    <TableCell className="font-medium text-ink">Total del pedido</TableCell>
                    <TableCell align="right" />
                    <TableCell align="right" />
                    <TableCell align="right" className="tabular font-semibold text-ink">
                      {formatNumber(detail.total)}
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </CardTableArea>
          )}
        </DetailSection>

        <div className="grid gap-4 sm:grid-cols-2">
          <DetailSection title="Cliente">
            <dl className="space-y-1 text-caption">
              <DetailRow label="Nombre" value={detail.clienteNombre} />
              <DetailRow label="Teléfono" value={detail.telefono ?? 'Sin teléfono'} />
              <DetailRow label="Creada el" value={formatDateTime(detail.createdAt)} />
              <DetailRow label="Último cambio" value={formatDateTime(detail.updatedAt)} />
              {detail.conversationId ? (
                <DetailRow
                  label="Conversación"
                  value={
                    <DetailLink
                      href={`${ROUTES.conversations}?conversacion=${detail.conversationId}`}
                    >
                      Abrir el chat
                    </DetailLink>
                  }
                />
              ) : null}
            </dl>
          </DetailSection>

          <DetailSection title="Entrega y pago">
            {addressLines.length === 0 ? (
              <p className="text-caption text-ink-faint">
                Sin dirección cargada: el pedido se cerró sin dato de entrega.
              </p>
            ) : (
              <ul className="space-y-0.5 text-caption text-ink-soft">
                {addressLines.map((line) => (
                  <li key={line}>{line}</li>
                ))}
              </ul>
            )}

            <p className="mt-2 text-caption text-ink-muted">
              {`Método de pago: ${detail.metodoPago ?? 'sin definir'}`}
            </p>
          </DetailSection>
        </div>

        <DetailSection title="Guía de envío">
          {detail.guiaEnvioFotoUrl ? (
            <dl className="space-y-1 text-caption">
              <DetailRow label="Número de guía" value={detail.guiaEnvioNumero ?? 'Sin número'} />
              <DetailRow
                label="Foto"
                value={<DetailLink href={detail.guiaEnvioFotoUrl}>Ver la guía</DetailLink>}
              />
            </dl>
          ) : (
            <p className="text-caption text-ink-faint">
              Sin guía cargada. Al cargarla, la orden pasa a «Enviada» y el cliente recibe en su chat
              la foto con el número.
            </p>
          )}
        </DetailSection>

        <DetailSection title="Historial de estados">
          {historial.length === 0 ? (
            <p className="text-caption text-ink-faint">
              {detailQuery.isPending
                ? 'Cargando el historial de la orden…'
                : 'Sin filas de historial todavía.'}
            </p>
          ) : (
            <ol className="space-y-2">
              {historial.map((entry) => (
                <li
                  key={entry.id}
                  className="rounded-panel border border-line bg-surface-muted/40 px-3 py-2"
                >
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <span className="text-caption font-medium text-ink">
                      {describeHistoryEntry(entry)}
                    </span>
                    <span className="text-micro text-ink-faint">
                      {formatDateTime(entry.createdAt)}
                    </span>
                  </div>

                  {entry.motivo ? (
                    <p className="mt-0.5 text-caption text-ink-muted">{entry.motivo}</p>
                  ) : null}

                  <p className="mt-0.5 text-micro text-ink-faint">
                    {entry.userId
                      ? 'Cambiado por un miembro del equipo'
                      : 'Automático (flujo del asistente o carga de la guía)'}
                  </p>
                </li>
              ))}
            </ol>
          )}
        </DetailSection>

        <p className="text-micro text-ink-faint">
          {destinos.length > 0
            ? `Desde «${actual.label}» la orden solo puede pasar a: ${destinos.map((item) => orderStatus[item].label).join(', ')}.`
            : `«${actual.label}» es un estado final: la orden ya cerró.`}
        </p>
      </div>
    </Modal>
  );
}
